import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { User } from "../api/api";
import { useKams, useProfile, useUsers } from "../hooks/queries";
import { useAuth } from "../hooks/useAuth";
import { actsAsKam } from "../lib/roles";
import { UserDialog, UserDialogTab } from "./gebruikers/UserDialog";

// "Instellingen" is the user dialog from Gebruikersbeheer, opened on the
// viewer's own account from anywhere in the app.

interface SettingsDialogContextValue {
  openSettings: (tab?: UserDialogTab) => void;
}

const SettingsDialogContext = createContext<SettingsDialogContextValue | null>(null);

export function useSettingsDialog() {
  const ctx = useContext(SettingsDialogContext);
  if (!ctx) throw new Error("useSettingsDialog must be used within SettingsDialogProvider");
  return ctx;
}

export function SettingsDialogProvider({ children, enabled }: { children: React.ReactNode; enabled: boolean }) {
  const [tab, setTab] = useState<UserDialogTab | null>(null);
  const openSettings = useCallback((t: UserDialogTab = "account") => setTab(t), []);
  const value = useMemo(() => ({ openSettings }), [openSettings]);

  return (
    <SettingsDialogContext.Provider value={value}>
      {children}
      {enabled && tab && <OwnUserDialog initialTab={tab} onClose={() => setTab(null)} />}
    </SettingsDialogContext.Provider>
  );
}

// Only admins may list users, so everyone else's own account is pieced
// together from the session and their profile (plus their partner grants
// from the KAM list, which every role can read).
function OwnUserDialog({ initialTab, onClose }: { initialTab: UserDialogTab; onClose: () => void }) {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const isKam = !!me && actsAsKam(me);
  const { data: profile } = useProfile();
  const { data: users } = useUsers(isAdmin);
  const { data: kams } = useKams(isKam && !isAdmin);

  const self = useMemo<User | null>(() => {
    if (!me || !profile) return null;
    if (isAdmin) return users?.find(u => u.id === me.id) ?? null;
    if (isKam && !kams) return null;
    return {
      id: me.id,
      name: profile.name,
      email: profile.email,
      role: me.role,
      partner_id: me.partner_id,
      kam_id: me.kam_id,
      secondary_kam_id: me.secondary_kam_id,
      branch_ids: me.branch_permissions ?? [],
      partner_ids: kams?.find(k => k.id === me.id)?.partner_ids ?? [],
      phone: profile.phone,
      can_act_as_kam: me.can_act_as_kam,
      profile_picture_url: profile.profile_picture_url,
    };
  }, [me, profile, isAdmin, users, isKam, kams]);

  if (!self) return null;
  return <UserDialog key={self.id} user={self} initialTab={initialTab} onClose={onClose} />;
}
